/*
    Roads Functions
*/

/* Rows Limit of Every Road */
const ROWS = 6


/* Normalize One Result
   Accepts both a plain letter ("P" | "B" | "T") and a full result object */
const normalize = (item) => {

    /* Check Item */
    if (!item) return null

    /* Letter Shortcut */
    if (typeof item === "string") {
        if (item !== "P" && item !== "B" && item !== "T") return null
        return { result: item, playerPair: false, bankerPair: false }
    }

    /* Check Result */
    if (item.result !== "P" && item.result !== "B" && item.result !== "T") return null

    return {
        result: item.result,
        playerPair: item.playerPair === true,
        bankerPair: item.bankerPair === true,
    }
}


/* Normalize Results List | Unknown entries are dropped */
const normalizeAll = (results = []) => results.map(normalize).filter(Boolean)


/* Build Bead Plate matrix */
export const buildBeadPlate = (results = [], rows = ROWS, minColumns = 18) => {

    /* Normalize Results */
    const list = normalizeAll(results)

    /* Determine Columns | The plate grows to the right so the newest results are never dropped */
    const cols = Math.max(minColumns, Math.ceil(list.length / rows))

    /* Prepare Grid */
    const grid = Array.from({ length: rows }, () => Array(cols).fill(null))

    /* Enter Results | Column by column, top to bottom */
    for (let i = 0; i < list.length; i++) {
        grid[i % rows][Math.floor(i / rows)] = list[i]
    }

    return grid
}


/* Build Big Road matrix */
export const buildBigRoad = (results = [], rows = ROWS, maxColumns = 200) => {

    /* Normalize Results */
    const list = normalizeAll(results)

    /* Prepare Grid */
    const grid = Array.from({ length: rows }, () => Array(maxColumns).fill(null))

    let last = null
    let col = 0
    let row = 0
    let runCol = 0
    let lastCell = null

    /* First Usable Column | Shifts to the right when the shoe opens with a tie */
    let startCol = 0

    const seekEmptyRight = (rr, cc) => {
        let c = cc
        while (c < maxColumns && grid[rr][c]) c++
        return c
    }

    const place = (item) => {

        col = seekEmptyRight(row, col)
        if (col >= maxColumns) return false

        lastCell = { result: item.result, ties: 0, playerPair: item.playerPair, bankerPair: item.bankerPair }
        grid[row][col] = lastCell
        return true
    }

    for (const item of list) {

        /* TIE */
        if (item.result === "T") {

            /* Opening Tie | The shoe has no Player / Banker result yet, so the tie gets its own cell */
            if (!last) {

                if (grid[0][0]) {
                    grid[0][0].ties += 1
                }
                else {
                    grid[0][0] = { result: "T", ties: 1, playerPair: item.playerPair, bankerPair: item.bankerPair }
                    startCol = 1
                }

                continue
            }

            /* Regular Tie | Marked on the last placed cell */
            if (lastCell) lastCell.ties += 1
            continue
        }

        /* FIRST RESULT */
        if (!last) {

            row = 0
            col = startCol
            runCol = startCol

            if (!place(item)) break
            last = item.result
            continue
        }

        /* SAME RESULT | Go down, or turn right when the cell below is taken (dragon tail) */
        if (item.result === last) {

            const down = row + 1

            if (down < rows && !grid[down][col]) {
                row = down
            }
            else {
                col = col + 1
            }

            if (!place(item)) break
            continue
        }

        /* NEW RESULT | New column right after the column the previous run started in */
        row = 0
        col = seekEmptyRight(row, runCol + 1)
        if (col >= maxColumns) break

        runCol = col
        if (!place(item)) break
        last = item.result
    }

    /* Trim Empty Columns */
    let usedMax = -1
    for (let c = 0; c < maxColumns; c++) {
        for (let r = 0; r < rows; r++) {
            if (grid[r][c]) usedMax = Math.max(usedMax, c)
        }
    }

    const end = Math.max(0, usedMax + 1)
    return grid.map((r) => r.slice(0, end))
}


/* Build Road From Signals */
export const buildRoadFromSignals = (signals = [], rows = ROWS, maxColumns = 200) => {

    const grid = Array.from({ length: rows }, () => Array(maxColumns).fill(null))

    let col = 0
    let row = 0
    let last = null
    let runCol = 0

    const put = (v, rr, cc) => {

        if (rr < 0 || rr >= rows) return false
        if (cc < 0 || cc >= maxColumns) return false

        if (!grid[rr][cc]) {
            grid[rr][cc] = { v }
            return true
        }

        return false
    }

    const seekEmptyRight = (rr, cc) => {
        let c = cc
        while (c < maxColumns && grid[rr][c]) c++
        return c
    }

    for (const x of signals) {

        if (!x) continue

        /* FIRST SIGNAL */
        if (!last) {
            col = 0
            row = 0
            runCol = 0
            put(x, row, col)
            last = x
            continue
        }

        /* SAME SIGNAL | Go down, or turn right when the cell below is taken */
        if (x === last) {

            const down = row + 1

            if (down < rows && !grid[down][col]) {
                row = down
                put(x, row, col)
            }
            else {
                col = seekEmptyRight(row, col + 1)
                if (col >= maxColumns) break
                put(x, row, col)
            }

            continue
        }

        /* NEW SIGNAL | New column right after the column the previous run started in */
        row = 0
        col = seekEmptyRight(row, runCol + 1)
        if (col >= maxColumns) break

        runCol = col
        put(x, row, col)
        last = x
    }

    /* Trim Empty Columns */
    let usedMax = -1
    for (let c = 0; c < maxColumns; c++) {
        for (let r = 0; r < rows; r++) {
            if (grid[r][c]) usedMax = Math.max(usedMax, c)
        }
    }

    const end = Math.max(0, usedMax + 1)
    return grid.map((r) => r.slice(0, end))
}


/* Build Big Road Logical Sequence
   Ties are skipped and the columns have no row limit on purpose:
   a derived road must count a dragon tail as a part of its own column */
export const buildBigRoadLogicalSequence = (results = []) => {

    const seq = []

    let last = null
    let c = -1
    let r = 0

    for (const item of normalizeAll(results)) {

        /* Ties Do Not Affect The Derived Roads */
        if (item.result === "T") continue

        if (item.result === last) {
            r = r + 1
        }
        else {
            c = c + 1
            r = 0
            last = item.result
        }

        seq.push({ c, r, result: item.result, isNewColumn: r === 0 })
    }

    return seq
}


/* Build Derived Signals
   k = 1 Big Eye Boy | k = 2 Small Road | k = 3 Cockroach Road */
export const buildDerivedSignals = (logicalSeq = [], k = 1) => {

    const hasCell = new Set()
    const colHeights = new Map()

    for (const it of logicalSeq) {
        hasCell.add(`${it.c}:${it.r}`)
        const h = colHeights.get(it.c) || 0
        if (it.r + 1 > h) colHeights.set(it.c, it.r + 1)
    }

    const signals = []

    for (const it of logicalSeq) {

        /* Starting Point | Second cell of column k, otherwise first cell of column k + 1 */
        if (it.c < k) continue
        if (it.c === k && it.r === 0) continue

        /* NEW COLUMN | Compare the heights of the two reference columns */
        if (it.isNewColumn) {
            const h1 = colHeights.get(it.c - 1) || 0
            const h2 = colHeights.get(it.c - (k + 1)) || 0
            signals.push(h1 === h2 ? "R" : "B")
        }

        /* SAME COLUMN | Compare the cell on the left with the one above it */
        else {
            const leftC = it.c - k
            const a = hasCell.has(`${leftC}:${it.r}`) ? 1 : 0
            const b = hasCell.has(`${leftC}:${it.r - 1}`) ? 1 : 0
            signals.push(a === b ? "R" : "B")
        }
    }

    return signals
}
